import { useCallback, useEffect, useState } from 'react';
import {
  currentSeason,
  type SeasonalSlot,
  type SeasonalWear,
  MAX_PICTURE_BYTES,
  PICTURE_SIZE,
  type Account,
  type GameId,
  type PlayerPlate,
  type PlayerStats,
  type Progress,
} from '@ofc/shared';

/** Only the fields sent are changed. A null title takes the title off. */
export interface AppearancePatch {
  color?: string;
  emoji?: string;
  plate?: string;
  title?: string | null;
  flourish?: string;
  skin?: string;
  font?: string;
  frame?: string;
  /** Null takes the catchphrase off. */
  catchphrase?: string | null;
  entrance?: string;
  banner?: string;
  /** A skin for one game; null puts that game back on the everywhere skin. */
  gameSkin?: { game: GameId; skin: string | null };
  /** Something worn for the event on now; null takes that slot off again. */
  seasonal?: { slot: SeasonalSlot; value: string | null };
}

/**
 * Optional accounts.
 *
 * The password never leaves the device. It is stretched here with PBKDF2 and only
 * the derived key is sent, because the Workers free plan's 10 ms CPU limit rules
 * out doing the stretching on the server.
 */

const SESSION_KEY = 'movie-game:session';
const PBKDF2_ITERATIONS = 150_000;

export interface Session {
  token: string;
  user: Account;
}

function toBase64(buffer: ArrayBuffer): string {
  let binary = '';
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/**
 * The salt is derived from the username rather than fetched, so signing in needs
 * no extra round trip and the server never has to confirm a name exists before a
 * password is checked.
 */
async function deriveKey(username: string, password: string): Promise<string> {
  const encoder = new TextEncoder();
  const base = await crypto.subtle.importKey('raw', encoder.encode(password), 'PBKDF2', false, [
    'deriveBits',
  ]);
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: encoder.encode(`movie-link:${username.toLowerCase()}`),
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    base,
    256,
  );
  return toBase64(bits);
}

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

function storeSession(session: Session | null): void {
  try {
    if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    // Storage blocked: the session works until the tab is closed.
  }
}

async function post<T>(path: string, body: unknown, token?: string): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(data.error ?? 'Something went wrong.');
  return data as T;
}

export async function fetchStats(username: string): Promise<PlayerStats | null> {
  const res = await fetch(`/api/stats/${encodeURIComponent(username)}`);
  if (!res.ok) return null;
  return (await res.json()) as PlayerStats;
}

/** Sign-in state, shared by the home screen and the room. */
export function useAccount() {
  const [session, setSession] = useState<Session | null>(loadSession);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Drop a session the server no longer recognises (expired, or a reset database).
  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    fetch('/api/auth/me', { headers: { authorization: `Bearer ${session.token}` } })
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 401) {
          storeSession(null);
          setSession(null);
          return;
        }
        // A session stored before a field existed would otherwise never learn it:
        // titles, flourishes and skins arrived long after some players signed in.
        const body = (await res.json().catch(() => null)) as { user?: Session['user'] } | null;
        if (!cancelled && body?.user) {
          setSession((current) => {
            if (!current || current.token !== session.token) return current;
            const next = { ...current, user: body.user! };
            storeSession(next);
            return next;
          });
        }
      })
      .catch(() => {
        // Offline: keep the session and let the next attempt decide.
      });
    return () => {
      cancelled = true;
    };
    // Only re-checked when a different token arrives.
  }, [session?.token]);

  const authenticate = useCallback(
    async (mode: 'login' | 'signup', username: string, password: string) => {
      setBusy(true);
      setError(null);
      try {
        const key = await deriveKey(username, password);
        const result = await post<Session>(`/api/auth/${mode}`, { username, key });
        storeSession(result);
        setSession(result);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Something went wrong.');
        return false;
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const signOut = useCallback(async () => {
    const token = session?.token;
    storeSession(null);
    setSession(null);
    if (token) await post('/api/auth/logout', {}, token).catch(() => {});
  }, [session?.token]);

  /**
   * Changes what the player wears.
   *
   * The server checks every item against what they have earned, so the stored
   * session is only updated once it has agreed. Failures are thrown rather than
   * swallowed: showing an item as equipped when the server refused it would
   * leave the profile saying one thing and the room seeing another.
   */
  const setAppearance = useCallback(
    async (patch: AppearancePatch) => {
      if (!session) return;
      await post('/api/auth/appearance', patch, session.token);
      const user = { ...session.user };
      if (patch.color !== undefined) user.avatarColor = patch.color;
      if (patch.emoji !== undefined) user.avatarEmoji = patch.emoji;
      if (patch.plate !== undefined) user.avatarPlate = patch.plate as PlayerPlate;
      if (patch.title !== undefined) user.avatarTitle = patch.title;
      if (patch.flourish !== undefined) user.avatarFlourish = patch.flourish;
      if (patch.skin !== undefined) user.skin = patch.skin;
      if (patch.font !== undefined) user.avatarFont = patch.font;
      if (patch.frame !== undefined) user.avatarFrame = patch.frame;
      if (patch.catchphrase !== undefined) user.avatarCatchphrase = patch.catchphrase;
      if (patch.entrance !== undefined) user.avatarEntrance = patch.entrance;
      if (patch.banner !== undefined) user.avatarBanner = patch.banner;
      if (patch.seasonal) {
        const season = currentSeason();
        if (season) {
          const wear: SeasonalWear =
            user.seasonal?.season === season.id ? { ...user.seasonal } : { season: season.id };
          if (patch.seasonal.value === null) delete wear[patch.seasonal.slot];
          else wear[patch.seasonal.slot] = patch.seasonal.value;
          user.seasonal = wear;
        }
      }
      if (patch.gameSkin) {
        const skins = { ...(user.gameSkins ?? {}) };
        if (patch.gameSkin.skin === null) delete skins[patch.gameSkin.game];
        else skins[patch.gameSkin.game] = patch.gameSkin.skin;
        user.gameSkins = skins;
      }
      const next: Session = { ...session, user };
      storeSession(next);
      setSession(next);
    },
    [session],
  );

  /** Level, achievements and unlocks, worked out fresh by the server. */
  const loadProgress = useCallback(async (): Promise<Progress | null> => {
    if (!session) return null;
    const res = await fetch('/api/auth/progress', {
      headers: { authorization: `Bearer ${session.token}` },
    });
    if (!res.ok) return null;
    return (await res.json()) as Progress;
  }, [session]);

  /**
   * Scales a chosen image down in the browser and sends the result.
   *
   * Resizing here rather than on the server is not only politeness: a Worker
   * gets ten milliseconds of CPU per request, which is nowhere near enough to
   * decode and resample a photograph. The canvas does it for free on a device
   * that has time to spare.
   */
  const setPicture = useCallback(
    async (file: File) => {
      if (!session) return;

      const bitmap = await createImageBitmap(file);
      const side = Math.min(bitmap.width, bitmap.height);
      const canvas = document.createElement('canvas');
      canvas.width = PICTURE_SIZE;
      canvas.height = PICTURE_SIZE;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not read that picture.');
      // Square crop from the middle, so a portrait is not squashed.
      ctx.drawImage(
        bitmap,
        (bitmap.width - side) / 2,
        (bitmap.height - side) / 2,
        side,
        side,
        0,
        0,
        PICTURE_SIZE,
        PICTURE_SIZE,
      );
      bitmap.close();

      const url = canvas.toDataURL('image/webp', 0.82);
      const [head, data] = url.split(',');
      const mime = /data:([^;]+)/.exec(head ?? '')?.[1] ?? 'image/webp';
      if (!data) throw new Error('Could not read that picture.');
      if (data.length > MAX_PICTURE_BYTES) throw new Error('That picture is too big.');

      await post('/api/auth/picture', { mime, data }, session.token);
      const next: Session = { ...session, user: { ...session.user, avatarPicture: true } };
      storeSession(next);
      setSession(next);
    },
    [session],
  );

  const clearPicture = useCallback(async () => {
    if (!session) return;
    await fetch('/api/auth/picture', {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${session.token}` },
    }).catch(() => {});
    const next: Session = { ...session, user: { ...session.user, avatarPicture: false } };
    storeSession(next);
    setSession(next);
  }, [session]);

  return {
    session,
    busy,
    error,
    setError,
    authenticate,
    signOut,
    setAppearance,
    loadProgress,
    setPicture,
    clearPicture,
  };
}
